import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Dices, Loader2, Shirt, Upload } from 'lucide-react';
import SignedImage from '@/components/ipe/SignedImage';
import { IPE_UPLOAD_ACCEPT } from '@/utils/ipeImageUploadFormats';
import { processImageUpload } from '@/utils/imageUpload';
import { imageStorageAPI } from '@/utils/api';
import { resolveUploadPreviewUrl } from '@/utils/imageUrlUtils';
import { showToast } from '@/utils/toast';
import {
  VIDEO_ASPECT_RATIO_OPTIONS,
  VIDEO_DEFAULT_ASPECT_RATIO,
  VIDEO_DEFAULT_MODEL_PROMPT_TYPE,
  VIDEO_DEFAULT_SEED,
  VIDEO_DURATION_MAX,
  VIDEO_DURATION_MIN,
  VIDEO_DURATION_PRESETS,
  VIDEO_DEFAULT_DURATION_SEC,
  VIDEO_GENERATION_MODE_OPTIONS,
  VIDEO_I2V_MODEL_IMAGE_PROMPT,
  VIDEO_I2V_SUPPLEMENT_MAX_LENGTH,
  VIDEO_MODEL_PROMPT_OPTIONS,
  VIDEO_SEED_MAX,
  VIDEO_SEED_MIN,
  VIDEO_T2V_PROMPT_MAX_LENGTH,
  createRandomVideoSeed,
  getVideoAspectRatioFrameStyle,
  normalizeVideoDurationSec,
  normalizeVideoGenerationMode,
  normalizeVideoModelPromptType,
  normalizeVideoSeed,
  resolveVideoModelPromptSnippet,
} from '@/pages/ipe/workbench/constants/videoStudioConfig';
import VideoT2vScriptPromptField from '@/pages/ipe/workbench/components/VideoT2vScriptPromptField';
import VideoI2vSupplementPromptField from '@/pages/ipe/workbench/components/VideoI2vSupplementPromptField';

const I2V_BASE_PROMPT = '将参考图生成自然流畅的商品展示视频';

const RATIO_HINT_KEY = {
  '16:9': 'r169',
  '9:16': 'r916',
  '1:1': 'r11',
  '3:4': 'r34',
  '4:3': 'r43',
  '4:5': 'r45',
  '5:4': 'r54',
  '2:3': 'r23',
  '3:2': 'r32',
  '21:9': 'r219',
  '9:21': 'r921',
};

const QUALITY_HINT_KEY = {
  '480P': 'q480',
  '720P': 'q720',
  '1080P': 'q1080',
};

const MODEL_LABEL_KEY = {
  none: ['modelNone', 'modelNoneHint'],
  female: ['modelFemale', 'modelFemaleHint'],
  male: ['modelMale', 'modelMaleHint'],
  kids: ['modelKids', 'modelKidsHint'],
  lifestyle: ['modelLife', 'modelLifeHint'],
};
/** 三列网格下常显两行（6 项），其余折叠 */
const VIDEO_RATIO_PRIMARY_COUNT = 6;

function renderRatioOption(item, { aspectRatio, onAspectRatioChange, disabled, t }) {
  const hintKey = RATIO_HINT_KEY[item.value];
  const hint = hintKey ? t(`studioChrome.${hintKey}`) : item.hint;
  const active = aspectRatio === item.value;
  const frameStyle = getVideoAspectRatioFrameStyle(item.value);
  return (
    <button
      key={item.value}
      type="button"
      className={`video-studio__ratio-btn${active ? ' is-active' : ''}`}
      onClick={() => onAspectRatioChange?.(item.value)}
      disabled={disabled}
      aria-pressed={active}
      title={`${item.label} · ${hint}`}
    >
      <span className="video-studio__ratio-frame-wrap" aria-hidden="true">
        <span className="video-studio__ratio-frame" style={frameStyle} />
      </span>
      <span className="video-studio__ratio-meta">
        <span className="video-studio__ratio-label">{item.label}</span>
        <span className="video-studio__ratio-hint">{hint}</span>
      </span>
    </button>
  );
}

/** 图生视频：可选模特图上传（侧栏紧凑版） */
function VideoModelImageField({
  modelImageUrl = '',
  modelImagePreviewUrl = '',
  disabled = false,
  onChange,
}) {
  const { t } = useTranslation('workbench');
  const fileRef = useRef(null);
  const dragCounterRef = useRef(0);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const hasImage = Boolean(String(modelImageUrl || '').trim());

  const clearImage = useCallback(() => {
    onChange?.({
      modelImageUrl: '',
      modelImagePreviewUrl: '',
    });
  }, [onChange]);

  const applyUpload = useCallback(async (file) => {
    if (!file?.type?.startsWith('image/') || disabled || uploading) return;
    setUploading(true);
    try {
      const item = await processImageUpload(file);
      let url = item?.url || item?.imageUrl || item?.serverUrl;
      let uploadResult = null;
      if (!url && item?.dataUrl) {
        uploadResult = await imageStorageAPI.save(item.dataUrl, { folder: 'image-space' });
        url = uploadResult?.url || uploadResult?.imageUrl;
      }
      if (!url) {
        throw new Error(t('videoModelImageField.uploadNotSaved'));
      }
      const trimmed = String(url).trim();
      const previewUrl = await resolveUploadPreviewUrl(trimmed, uploadResult);
      onChange?.({
        modelImageUrl: trimmed,
        modelImagePreviewUrl: previewUrl || trimmed,
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [disabled, onChange, uploading, t]);

  const onFiles = useCallback(async (fileList) => {
    const file = Array.from(fileList || []).find((entry) => entry?.type?.startsWith('image/'));
    if (!file) return;
    try {
      await applyUpload(file);
    } catch (error) {
      showToast(error?.message || t('videoModelImageField.uploadFailed'), 'error');
    }
  }, [applyUpload, t]);

  const triggerFileSelect = () => {
    if (disabled || uploading) return;
    fileRef.current?.click();
  };

  return (
    <section className="video-studio__section">
      <span className="video-studio__field-label">{t('studioChrome.modelImage')}</span>
      <p className="video-studio__field-hint">
        {t('studioChrome.modelHint')}
      </p>
      <input
        ref={fileRef}
        type="file"
        accept={IPE_UPLOAD_ACCEPT}
        onChange={(event) => onFiles(event.target.files)}
        className="img2img-studio__file-input"
        disabled={disabled || uploading}
        tabIndex={-1}
      />
      <button
        type="button"
        className={`video-studio__model-dropzone${isDragOver ? ' is-dragover' : ''}${hasImage ? ' is-filled' : ''}${uploading ? ' is-busy' : ''}`}
        onClick={triggerFileSelect}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          dragCounterRef.current += 1;
          setIsDragOver(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
          if (dragCounterRef.current === 0) setIsDragOver(false);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          dragCounterRef.current = 0;
          setIsDragOver(false);
          if (disabled || uploading) return;
          onFiles(event.dataTransfer?.files);
        }}
        disabled={disabled || uploading}
        aria-label={t('studioChrome.uploadModel')}
      >
        {hasImage ? (
          <>
            <div className="video-studio__model-preview">
              <SignedImage
                url={modelImageUrl}
                displayUrl={modelImagePreviewUrl || modelImageUrl}
                alt={t('studioChrome.modelAlt')}
              />
            </div>
            <div className="video-studio__model-filled-body">
              <p className="video-studio__model-title">{t('studioChrome.modelUploaded')}</p>
              <p className="video-studio__model-hint">{t('studioChrome.modelReplace')}</p>
              <div className="video-studio__model-actions">
                <button
                  type="button"
                  className="video-studio__model-link"
                  onClick={(event) => {
                    event.stopPropagation();
                    triggerFileSelect();
                  }}
                  disabled={disabled || uploading}
                >
                  {t('studioChrome.reupload')}
                </button>
                <button
                  type="button"
                  className="video-studio__model-link video-studio__model-link--danger"
                  onClick={(event) => {
                    event.stopPropagation();
                    clearImage();
                  }}
                  disabled={disabled}
                >
                  {t('studioChrome.remove')}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="video-studio__model-icon-wrap" aria-hidden="true">
              <Shirt size={28} strokeWidth={1.5} />
              <span className="video-studio__model-badge">
                <Upload size={12} strokeWidth={2.2} />
              </span>
            </div>
            <div className="video-studio__model-filled-body">
              <p className="video-studio__model-title">{t('studioChrome.uploadModelOptional')}</p>
              <p className="video-studio__model-hint">{t('studioChrome.formatHint')}</p>
              {uploading ? (
                <p className="video-studio__model-status">
                  <Loader2 size={14} className="img2img-studio__spin" aria-hidden="true" />
                  {t('studioChrome.uploading')}
                </p>
              ) : null}
            </div>
          </>
        )}
      </button>
    </section>
  );
}

/**
 * 图生/文生视频参数表单（侧栏嵌入）
 */
export default function VideoTaskStudioFields({
  scriptPrompt = '',
  onScriptPromptChange,
  scriptPromptMaxLength = VIDEO_T2V_PROMPT_MAX_LENGTH,
  showScriptPrompt = false,
  supplementPrompt = '',
  onSupplementPromptChange,
  showSupplementPrompt = false,
  supplementPromptMaxLength = VIDEO_I2V_SUPPLEMENT_MAX_LENGTH,
  /** 图生：是否展示模特图上传 */
  showModelImage = false,
  modelImageUrl = '',
  modelImagePreviewUrl = '',
  onModelImageChange,
  /** 文生：是否展示模特提示词类型 */
  showModelPromptType = false,
  modelPromptType = VIDEO_DEFAULT_MODEL_PROMPT_TYPE,
  onModelPromptTypeChange,
  genMode,
  onGenModeChange,
  /** 视频画面比例区域：文生/图生视频均可展示（图生视频由服务端按比例处理） */
  showAspectRatio = true,
  aspectRatio = VIDEO_DEFAULT_ASPECT_RATIO,
  onAspectRatioChange,
  /** 可选比例列表（如 Temu 场景仅 1:1 / 3:4 / 16:9） */
  aspectRatioOptions,
  durationSec = VIDEO_DEFAULT_DURATION_SEC,
  onDurationSecChange,
  seed = VIDEO_DEFAULT_SEED,
  onSeedChange,
  disabled = false,
  /** 模板库/新增模板居中弹窗（工作流节点参数面板内使用） */
  caseOverlayCentered = false,
}) {
  const { t } = useTranslation('workbench');
  const safeDurationSec = normalizeVideoDurationSec(durationSec);
  const safeSeed = normalizeVideoSeed(seed);
  const safeModelPromptType = normalizeVideoModelPromptType(modelPromptType);
  const durationProgress = ((safeDurationSec - VIDEO_DURATION_MIN) / (VIDEO_DURATION_MAX - VIDEO_DURATION_MIN)) * 100;
  const supplementMax = Math.max(1, Number(supplementPromptMaxLength) || VIDEO_I2V_SUPPLEMENT_MAX_LENGTH);
  const ratioOptionPool = Array.isArray(aspectRatioOptions) && aspectRatioOptions.length
    ? aspectRatioOptions
    : VIDEO_ASPECT_RATIO_OPTIONS;
  // 兜底：当前值不在候选列表（如历史 9:16）时补齐，避免出现「无选中项」
  const ratioOptions = ratioOptionPool.some((item) => item.value === aspectRatio)
    ? ratioOptionPool
    : [...ratioOptionPool, ...VIDEO_ASPECT_RATIO_OPTIONS.filter((item) => item.value === aspectRatio)];
  const primaryRatioOptions = ratioOptions.slice(0, VIDEO_RATIO_PRIMARY_COUNT);
  const extraRatioOptions = ratioOptions.slice(VIDEO_RATIO_PRIMARY_COUNT);
  const hasExtraRatioOptions = extraRatioOptions.length > 0;
  const isExtraRatioSelected = extraRatioOptions.some((item) => item.value === aspectRatio);
  const [ratioExpanded, setRatioExpanded] = useState(isExtraRatioSelected);

  useEffect(() => {
    if (isExtraRatioSelected) setRatioExpanded(true);
  }, [isExtraRatioSelected]);

  const handleSeedInputChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      onSeedChange?.(VIDEO_SEED_MIN);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    onSeedChange?.(normalizeVideoSeed(n));
  };

  const ratioOptionProps = { aspectRatio, onAspectRatioChange, disabled, t };

  return (
    <>
      {showModelImage ? (
        <VideoModelImageField
          modelImageUrl={modelImageUrl}
          modelImagePreviewUrl={modelImagePreviewUrl}
          disabled={disabled}
          onChange={onModelImageChange}
        />
      ) : null}

      {showSupplementPrompt ? (
        <VideoI2vSupplementPromptField
          prompt={supplementPrompt}
          onPromptChange={onSupplementPromptChange}
          generationMode={genMode}
          onGenerationModeChange={onGenModeChange}
          aspectRatio={aspectRatio}
          onAspectRatioChange={onAspectRatioChange}
          durationSec={durationSec}
          onDurationSecChange={onDurationSecChange}
          seed={seed}
          onSeedChange={onSeedChange}
          disabled={disabled}
          maxLength={supplementMax}
          textareaId="video-i2v-supplement-prompt"
          caseOverlayCentered={caseOverlayCentered}
        />
      ) : null}

      {showScriptPrompt ? (
        <VideoT2vScriptPromptField
          prompt={scriptPrompt}
          onPromptChange={onScriptPromptChange}
          generationMode={genMode}
          onGenerationModeChange={onGenModeChange}
          aspectRatio={aspectRatio}
          onAspectRatioChange={onAspectRatioChange}
          durationSec={durationSec}
          onDurationSecChange={onDurationSecChange}
          seed={seed}
          onSeedChange={onSeedChange}
          disabled={disabled}
          maxLength={scriptPromptMaxLength}
          textareaId="video-t2v-script-prompt"
          caseOverlayCentered={caseOverlayCentered}
        />
      ) : null}

      {showModelPromptType ? (
        <section className="video-studio__section">
          <span className="video-studio__field-label">{t('studioChrome.modelPrompt')}</span>
          <p className="video-studio__field-hint">
            {t('studioChrome.modelPromptHint')}
          </p>
          <div className="video-studio__model-prompt-grid" role="group" aria-label={t('studioChrome.modelPrompt')}>
            {VIDEO_MODEL_PROMPT_OPTIONS.map((option) => {
              const active = safeModelPromptType === option.value;
              const labelKeys = MODEL_LABEL_KEY[option.value];
              const label = labelKeys ? t(`studioChrome.${labelKeys[0]}`) : option.label;
              const hint = labelKeys ? t(`studioChrome.${labelKeys[1]}`) : option.hint;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`video-studio__model-prompt-btn${active ? ' is-active' : ''}`}
                  onClick={() => onModelPromptTypeChange?.(option.value)}
                  disabled={disabled}
                  aria-pressed={active}
                  title={hint}
                >
                  <span className="video-studio__model-prompt-title">{label}</span>
                  <span className="video-studio__model-prompt-hint">{hint}</span>
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className="video-studio__section">
        <span className="video-studio__field-label">{t('studioChrome.quality')}</span>
        <div className="video-studio__gen-modes" role="group" aria-label={t('studioChrome.quality')}>
          {VIDEO_GENERATION_MODE_OPTIONS.map((option) => {
            const active = normalizeVideoGenerationMode(genMode) === option.value;
            const hintKey = QUALITY_HINT_KEY[option.value];
            const hint = hintKey ? t(`studioChrome.${hintKey}`) : option.hint;
            return (
              <button
                key={option.value}
                type="button"
                className={`video-studio__gen-mode${active ? ' is-active' : ''}`}
                onClick={() => onGenModeChange?.(option.value)}
                disabled={disabled}
                aria-pressed={active}
              >
                <span className="video-studio__gen-mode-title">{option.label}</span>
                <span className="video-studio__gen-mode-desc">{hint}</span>
              </button>
            );
          })}
        </div>
      </section>

      {showAspectRatio ? (
      <section className="video-studio__section">
        <span className="video-studio__field-label">{t('studioChrome.ratio')}</span>
        <div className="video-studio__ratio-panel">
          <div className="video-studio__ratio-grid" role="group" aria-label={t('studioChrome.commonRatios')}>
            {primaryRatioOptions.map((item) => renderRatioOption(item, ratioOptionProps))}
          </div>
          <div
            className={`video-studio__ratio-extra${ratioExpanded ? ' is-expanded' : ''}`}
            aria-hidden={!ratioExpanded}
            inert={!ratioExpanded ? true : undefined}
          >
            <div className="video-studio__ratio-extra-inner">
              <div className="video-studio__ratio-grid" role="group" aria-label={t('studioChrome.extraRatios')}>
                {extraRatioOptions.map((item) => renderRatioOption(item, ratioOptionProps))}
              </div>
            </div>
          </div>
          {hasExtraRatioOptions ? (
            <button
              type="button"
              className={`video-studio__ratio-fold${ratioExpanded ? ' is-expanded' : ''}`}
              onClick={() => setRatioExpanded((open) => !open)}
              disabled={disabled}
              aria-expanded={ratioExpanded}
              aria-label={ratioExpanded ? t('studioChrome.lessRatio') : t('studioChrome.moreRatio')}
            >
              <ChevronDown
                size={16}
                strokeWidth={2.25}
                className="video-studio__ratio-fold-icon"
                aria-hidden="true"
              />
            </button>
          ) : null}
        </div>
      </section>
      ) : null}

      <section className="video-studio__section">
        <span className="video-studio__field-label">{t('studioChrome.duration')}</span>
        <div className="video-studio__duration">
          <div className="video-studio__duration-presets" role="group" aria-label={t('studioChrome.commonDuration')}>
            {VIDEO_DURATION_PRESETS.map((sec) => (
              <button
                key={sec}
                type="button"
                className={`video-studio__duration-preset${safeDurationSec === sec ? ' is-active' : ''}`}
                onClick={() => onDurationSecChange?.(sec)}
                disabled={disabled}
                aria-pressed={safeDurationSec === sec}
              >
                {sec}s
              </button>
            ))}
          </div>
          <div className="video-studio__duration-slider">
            <span className="video-studio__duration-slider-label">{t('studioChrome.fineTune')}</span>
            <div
              className="video-studio__duration-track"
              style={{ '--duration-progress': `${durationProgress}%` }}
            >
              <input
                type="range"
                className="video-studio__duration-input"
                min={VIDEO_DURATION_MIN}
                max={VIDEO_DURATION_MAX}
                step={1}
                value={safeDurationSec}
                disabled={disabled}
                onChange={(e) => onDurationSecChange?.(normalizeVideoDurationSec(e.target.value))}
                style={{ '--duration-progress': `${durationProgress}%` }}
                aria-label={t('studioChrome.fineTune')}
                aria-valuemin={VIDEO_DURATION_MIN}
                aria-valuemax={VIDEO_DURATION_MAX}
                aria-valuenow={safeDurationSec}
              />
              <span
                className="video-studio__duration-thumb-value"
                style={{ left: `${durationProgress}%` }}
                aria-hidden="true"
              >
                {safeDurationSec}s
              </span>
            </div>
            <span className="video-studio__duration-bounds" aria-hidden="true">
              <span>{VIDEO_DURATION_MIN}s</span>
              <span>{VIDEO_DURATION_MAX}s</span>
            </span>
          </div>
        </div>
      </section>

      <section className="video-studio__section">
        <label className="video-studio__field-label" htmlFor="video-studio-seed">
          {t('studioChrome.seedLabel')}
        </label>
        <div className="video-studio__seed-row">
          <input
            id="video-studio-seed"
            type="number"
            className="video-studio__seed-input"
            min={VIDEO_SEED_MIN}
            max={VIDEO_SEED_MAX}
            step={1}
            inputMode="numeric"
            value={safeSeed}
            onChange={handleSeedInputChange}
            disabled={disabled}
            aria-label={t('studioChrome.seedAria')}
          />
          <button
            type="button"
            className="video-studio__seed-random"
            onClick={() => onSeedChange?.(createRandomVideoSeed())}
            disabled={disabled}
            title={t('studioChrome.seedRandomTitle')}
            aria-label={t('studioChrome.seedRandomAria')}
          >
            <Dices size={16} strokeWidth={1.75} aria-hidden="true" />
            <span>{t('studioChrome.seedRandom')}</span>
          </button>
        </div>
      </section>
    </>
  );
}

export function buildVideoConfigPayload({
  genMode,
  aspectRatio,
  durationSec,
  seed,
  supplementPrompt,
  modelPromptType,
  modelImageUrl,
  modelImagePreviewUrl,
}) {
  const payload = {
    generationMode: normalizeVideoGenerationMode(genMode),
    aspectRatio: aspectRatio || VIDEO_DEFAULT_ASPECT_RATIO,
    durationSec: normalizeVideoDurationSec(durationSec),
    seed: normalizeVideoSeed(seed),
  };
  if (supplementPrompt !== undefined) {
    payload.supplementPrompt = String(supplementPrompt || '').trim().slice(0, VIDEO_I2V_SUPPLEMENT_MAX_LENGTH);
  }
  if (modelPromptType !== undefined) {
    payload.modelPromptType = normalizeVideoModelPromptType(modelPromptType);
  }
  const modelUrl = String(modelImageUrl || '').trim();
  if (modelUrl) {
    payload.modelImageUrl = modelUrl;
    payload.modelImagePreviewUrl = String(modelImagePreviewUrl || modelUrl).trim() || modelUrl;
  }
  return payload;
}

/** 图生视频：基础提示 + 可选模特出镜 + 用户补充 */
export function resolveI2vAgentPrompt({ supplementPrompt = '', hasModelImage = false } = {}) {
  const parts = [I2V_BASE_PROMPT];
  if (hasModelImage) {
    parts.push(VIDEO_I2V_MODEL_IMAGE_PROMPT);
  }
  const sup = String(supplementPrompt || '').trim().slice(0, VIDEO_I2V_SUPPLEMENT_MAX_LENGTH);
  if (sup) {
    parts.push(`用户补充要求：${sup}`);
  }
  return parts.join('。');
}

/** 图生/文生视频提交时的最终 prompt */
export function resolveVideoSubmitPrompt({
  isT2V = false,
  scriptPrompt = '',
  supplementPrompt = '',
  modelPromptType = VIDEO_DEFAULT_MODEL_PROMPT_TYPE,
  hasModelImage = false,
} = {}) {
  if (isT2V) {
    const base = String(scriptPrompt || '').trim();
    const snippet = resolveVideoModelPromptSnippet(modelPromptType);
    if (!snippet) return base;
    if (!base) return snippet;
    return `${base}。${snippet}`;
  }
  return resolveI2vAgentPrompt({ supplementPrompt, hasModelImage });
}

export function resolvePrimaryVideoImageUrl(references) {
  const first = Array.isArray(references) ? references[0] : null;
  if (!first) return '';
  const raw = first.apiUrl || first.previewUrl || first.src || first.url || '';
  return raw ? String(raw).trim() : '';
}
